import { useEffect, useState } from 'react';
import { X, Loader2, CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react';

export default function ExecutionsPanel({ api, companyId, workflowId, onClose }) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getWorkflowExecutions(companyId, workflowId);
      setExecutions(Array.isArray(data) ? data : data?.executions || []);
    } catch {
      setExecutions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [workflowId]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'failed':
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
      case 'completed':
        return 'bg-emerald-50 text-emerald-700';
      case 'failed':
      case 'error':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-amber-50 text-amber-700';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="w-[340px] bg-white border-l border-gray-200 h-full flex flex-col shrink-0 shadow-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">Executions</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={load}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5 text-gray-400" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        ) : executions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Clock className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-400 text-center">No executions yet</p>
            <p className="text-xs text-gray-300 text-center mt-1">Run or activate the workflow to see executions here.</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {executions.map((exec, i) => {
              const isExpanded = expanded === i;
              return (
                <div key={exec.id || i} className="rounded-lg border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => setExpanded(isExpanded ? null : i)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-gray-50 transition"
                  >
                    {getStatusIcon(exec.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize ${getStatusColor(exec.status)}`}>
                          {exec.status || 'unknown'}
                        </span>
                        {exec.trigger && (
                          <span className="text-[10px] text-gray-400">{exec.trigger}</span>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {formatDate(exec.startedAt || exec.createdAt || exec.date)}
                        {exec.duration != null && ` · ${exec.duration}ms`}
                      </p>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-50">
                      {exec.steps && exec.steps.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Steps</p>
                          {exec.steps.map((step, si) => (
                            <div key={si} className="flex items-center gap-2 text-[11px]">
                              {getStatusIcon(step.status)}
                              <span className="text-gray-700 truncate">{step.nodeLabel || step.nodeType || `Step ${si + 1}`}</span>
                              {step.duration != null && (
                                <span className="text-gray-400 ml-auto shrink-0">{step.duration}ms</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {exec.error && (
                        <div className="mt-2">
                          <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wide mb-1">Error</p>
                          <pre className="text-[10px] text-red-600 bg-red-50 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all">
                            {typeof exec.error === 'object' ? JSON.stringify(exec.error, null, 2) : exec.error}
                          </pre>
                        </div>
                      )}
                      {exec.output && (
                        <div className="mt-2">
                          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Output</p>
                          <pre className="text-[10px] text-gray-600 bg-gray-50 rounded-md p-2 overflow-x-auto max-h-32 overflow-y-auto whitespace-pre-wrap break-all">
                            {typeof exec.output === 'object' ? JSON.stringify(exec.output, null, 2) : exec.output}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
